/**
 * DocumentInitializationDetector - Utility for detecting document initialization state transitions
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility detects state transitions that require document re-initialization,
 * such as transitioning from empty text to non-empty text.
 */

/**
 * Result of initialization state detection
 */
export interface InitializationState {
  wasEmpty: boolean;
  isNowNonEmpty: boolean;
  isEmptyToNonEmptyTransition: boolean;
  requiresReinitialization: boolean;
}

/**
 * Detect if document initialization state has changed
 * 
 * Determines if we're transitioning from empty to non-empty text, which requires
 * re-initialization of the WASM document state.
 * 
 * @param previousTextLength - Length of text from previous initialization (0 if never initialized)
 * @param currentTextLength - Length of text for current initialization
 * @param isDocumentInitialized - Whether document was previously initialized
 * @param forceReinitialize - Whether re-initialization is explicitly forced
 * @returns Initialization state with transition detection flags
 */
export function detectInitializationState(
  previousTextLength: number,
  currentTextLength: number,
  isDocumentInitialized: boolean,
  forceReinitialize: boolean = false
): InitializationState {
  const wasEmpty = previousTextLength === 0;
  const isNowNonEmpty = currentTextLength > 0;
  const isEmptyToNonEmptyTransition = wasEmpty && isNowNonEmpty;
  
  // Require re-initialization if:
  // 1. Explicitly forced
  // 2. Transitioning from empty to non-empty (race condition fix)
  // 3. Not already initialized
  const requiresReinitialization = forceReinitialize || 
                                   isEmptyToNonEmptyTransition || 
                                   !isDocumentInitialized;
  
  return {
    wasEmpty,
    isNowNonEmpty,
    isEmptyToNonEmptyTransition,
    requiresReinitialization
  };
}

/**
 * Check if document should skip initialization
 * 
 * Determines if initialization should be skipped because document is already
 * initialized and no state transition requires re-initialization.
 * 
 * @param state - Initialization state from detectInitializationState
 * @returns True if initialization should be skipped
 */
export function shouldSkipInitialization(state: InitializationState): boolean {
  // Skip if already initialized and no transition requires re-initialization
  return !state.requiresReinitialization;
}

